import { useMemo } from "react";
import { useIntl } from "react-intl";
import { CommandBarButton, ContextualMenuItemType, ICommandBarItemProps } from "@fluentui/react";
import { ExportMode } from "@cayo/shared";
import { ExportConfig, ExportFormat } from "@cayo/shared/api/types/schema";
import { IQuery, DispatchAction } from "@cayo/shared/components/GridContainer/types";
import { resizeAndGroupsItems } from "./useResetColumnsAndGroupButton";
import { commandBarMessages } from "../../CommandBar/messages";
import { getExportIconName } from "../components/ExportButton/utils/getExportIconName";
import { GroupConfig } from "../GridFilterBar";
import { hideCheckmark, hideIcon, useQuickFilterBarButtonStyle } from "../GridFilterBar.style";
import { localizedExportMode } from "../utils/localizedExportMode";

type GridContextMenuProps = {
  dispatch: DispatchAction;
  query: IQuery;
  allowClear: boolean;
  groupConfig?: GroupConfig[];
  previewQueryId?: string | null;
  onExport?: (format: ExportFormat, mode: ExportMode) => void;
  exportDisabled?: boolean;
  exportConfig?: ExportConfig;
};

export const useGridContextMenu = ({
  dispatch,
  query,
  allowClear,
  groupConfig,
  previewQueryId,
  onExport,
  exportDisabled,
  exportConfig,
}: GridContextMenuProps) => {
  const intl = useIntl();
  const menuStyles = useQuickFilterBarButtonStyle();

  const greedOptionsMenu = useMemo(() => {
    return resizeAndGroupsItems(intl, dispatch, groupConfig ?? [], query, true, allowClear);
  }, [intl, dispatch, groupConfig, query.groupBy, allowClear]);

  const exportMenuItems = useMemo(() => {
    const showExportButton = exportConfig?.enabled && exportConfig.formats.length > 0 && onExport;

    if (!showExportButton) {
      return [];
    }

    const separator: ICommandBarItemProps = {
      key: "export-separator",
      itemType: 1,
    };

    const exportTitle: ICommandBarItemProps = {
      key: "export-title",
      text: "Export",
      itemType: ContextualMenuItemType.Header,
      style: { fontSize: 16, fontWeight: 600 },
      itemProps: hideCheckmark,
      iconProps: hideIcon,
    };

    const buildExportMenuItem = (format: ExportFormat, mode: ExportMode): ICommandBarItemProps => ({
      key: format,
      text: intl.formatMessage(commandBarMessages.exportModeTo, {
        mode: localizedExportMode(mode, intl),
        format: format.toUpperCase(),
      }),
      onClick: () => onExport(format, mode),
      disabled: exportDisabled,
      iconProps: { iconName: getExportIconName(format) },
    });

    const exportItems = exportConfig.formats
      .map((format) => [
        buildExportMenuItem(format, "visible"),
        buildExportMenuItem(format, "selected"),
      ])
      .flat();

    return [separator, exportTitle, ...exportItems];
  }, [exportConfig, onExport, exportDisabled]);

  const gridContextMenu: ICommandBarItemProps = useMemo(() => {
    return {
      key: "clearButton",
      onRender: () => (
        <CommandBarButton
          primaryDisabled={!allowClear}
          iconProps={{ iconName: "ClearFilter" }}
          ariaLabel="Clear filter"
          splitButtonAriaLabel="See options"
          title={intl.formatMessage(commandBarMessages.clearFilter)}
          styles={menuStyles}
          onClick={() =>
            allowClear ? dispatch({ kind: "clearQuery", previewQuery: !!previewQueryId }) : null
          }
          split
          menuProps={{ items: [...greedOptionsMenu, ...exportMenuItems] }}
        />
      ),
    };
  }, [dispatch, query, previewQueryId, exportMenuItems]);

  return { gridContextMenu, gridContextMenuStyle: menuStyles };
};
